import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useUpdateCurrentUser } from 'src/features/profile/api/useUpdateCurrentUser';
import { detailsMessages } from 'src/features/profile/utils/messages';
import {
    UpdateCurrentUserFormSchemaValues,
    updateCurrentUserFormSchema,
} from 'src/features/profile/utils/formSchema';
import {
    FormFieldBlock,
    FormFieldBlockBody,
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockSeparator,
    FormFieldBlockSubtitle,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

import type { User } from 'src/features/auth/types';

type ProfileInfoFormProps = {
    data: User;
    refetch: () => void;
};

const ProfileInfoForm = ({ data, refetch }: ProfileInfoFormProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<UpdateCurrentUserFormSchemaValues>({
        resolver: zodResolver(updateCurrentUserFormSchema),
        mode: 'onChange',
        defaultValues: {
            name: data.name ?? '',
            email: data.email ?? '',
        },
    });

    const { mutate, isPending } = useUpdateCurrentUser({
        onSuccess: ({ name, email }) => {
            form.reset({ name, email });
            refetch();
            toast({
                title: formatMessage(detailsMessages.updateSuccess),
                description: formatMessage(detailsMessages.updateSuccessDetails),
            });
        },
        onError: (e) => {
            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: e?.response?.data?.errors?.[0]?.detail ?? '',
            });
        },
    });

    React.useEffect(() => {
        void form.trigger();
    }, []);

    const onSubmit = (values: UpdateCurrentUserFormSchemaValues) => mutate(values);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(detailsMessages.name)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(detailsMessages.nameSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="text" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(detailsMessages.email)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>{formatMessage(detailsMessages.emailSubtitle)}</FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <Input type="email" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
                >
                    {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default ProfileInfoForm;
